import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { CaretDown, CaretRight, Check, Plus, Sparkle, Trash, X } from "@phosphor-icons/react";
import { ChipSelector, CopyButton, LoadingSpinner, PlatformBadge } from "@/components/shared";
import { API } from "@/lib/api";

const ContentPipeline = () => {
  const [pieces, setPieces] = useState([]);
  const [suggestions, setSuggestions] = useState(null);
  const [dailyContent, setDailyContent] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newPiece, setNewPiece] = useState({ title: "", platform: "tiktok" });
  const [loading, setLoading] = useState(true);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [loadingDaily, setLoadingDaily] = useState(false);
  const [expandedStage, setExpandedStage] = useState(null);
  const [selectedPiece, setSelectedPiece] = useState(null);

  // Daily affirmation — rotates based on day of year
  const affirmations = [
    "You're building something that matters. Keep going.",
    "Your brand is your legacy. Every post brings you closer.",
    "Consistency is your superpower. Show up today.",
    "The content you create today plants seeds for tomorrow's success.",
    "You didn't come this far to only come this far.",
    "Your voice matters. Somebody needs to hear exactly what you have to say.",
    "Progress over perfection. Hit publish.",
    "You're not behind. You're building at your own pace, and that's powerful.",
    "Every piece of content is a brick in your empire.",
    "The algorithm rewards consistency. So does life.",
    "Your audience is waiting for you. Don't keep them waiting.",
    "What you're building is bigger than one post. Trust the process.",
    "Discipline today, freedom tomorrow. Keep creating.",
    "You're not just creating content — you're creating opportunities.",
    "Small daily actions lead to massive results. You've got this.",
    "Your story is your strategy. Tell it boldly.",
    "Rest if you must, but don't quit. Your breakthrough is coming.",
    "The world needs your unique perspective. Share it.",
    "Invest in your brand today. Future you will be grateful.",
    "You are the brand. Everything you create reflects your power.",
    "One video away from changing someone's life. Keep posting.",
    "Clarity comes from action, not thought. Create something today.",
    "Your content is your resume. Make it count.",
    "Doubt kills more dreams than failure ever will. Post it anyway.",
    "Today's effort is tomorrow's empire. Build relentlessly.",
    "You're already ahead of everyone who didn't start. Keep moving.",
    "Great brands aren't built overnight. They're built daily.",
    "Your next post could be the one that changes everything.",
    "Show up messy. Show up scared. Just show up.",
    "The best time to post was yesterday. The next best time is now.",
    "You are closer than you think. One more push.",
  ];

  // Week calendar & date references
  const today = new Date();
  const dayOfYear = Math.floor((today - new Date(today.getFullYear(), 0, 0)) / 86400000);
  const todayAffirmation = affirmations[dayOfYear % affirmations.length];

  const stages = ["idea", "scripted", "filmed", "editing", "ready", "posted"];
  const stageLabels = {
    idea: "Ideas",
    scripted: "Scripted",
    filmed: "Filmed",
    editing: "Editing",
    ready: "Ready",
    posted: "Posted",
  };
  const stageColors = {
    idea: "bg-zinc-100 text-zinc-600",
    scripted: "bg-amber-100 text-amber-700",
    filmed: "bg-blue-100 text-blue-700",
    editing: "bg-purple-100 text-purple-700",
    ready: "bg-green-100 text-green-700",
    posted: "bg-[#E91E8C]/10 text-[#E91E8C]",
  };
  const startOfWeek = new Date(today);
  startOfWeek.setDate(today.getDate() - today.getDay());
  const weekDays = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(startOfWeek);
    d.setDate(startOfWeek.getDate() + i);
    return d;
  });
  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  const fetchPipeline = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/pipeline`);
      setPieces(res.data);
    } catch (e) {
      console.error("Error fetching pipeline:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSuggestions = async () => {
    setLoadingSuggestions(true);
    try {
      const res = await axios.get(`${API}/pipeline/suggestions`);
      setSuggestions(res.data);
    } catch (e) {
      console.error("Error fetching suggestions:", e);
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const generateDailyContent = async () => {
    setLoadingDaily(true);
    setDailyContent(null);
    try {
      const res = await axios.post(`${API}/pipeline/generate-daily`);
      setDailyContent(res.data);
    } catch (e) {
      console.error("Error generating daily content:", e);
    } finally {
      setLoadingDaily(false);
    }
  };

  useEffect(() => {
    fetchPipeline();
  }, [fetchPipeline]);

  const addPiece = async () => {
    if (!newPiece.title) return;
    try {
      await axios.post(`${API}/pipeline`, newPiece);
      setNewPiece({ title: "", platform: "tiktok" });
      setShowAddModal(false);
      fetchPipeline();
    } catch (e) {
      console.error("Error adding piece:", e);
    }
  };

  const advanceStage = async (piece) => {
    const currentIndex = stages.indexOf(piece.stage);
    if (currentIndex < stages.length - 1) {
      const nextStage = stages[currentIndex + 1];
      try {
        await axios.patch(`${API}/pipeline/${piece.id}`, { stage: nextStage });
        fetchPipeline();
      } catch (e) {
        console.error("Error advancing stage:", e);
      }
    }
  };

  const deletePiece = async (id) => {
    try {
      await axios.delete(`${API}/pipeline/${id}`);
      fetchPipeline();
    } catch (e) {
      console.error("Error deleting piece:", e);
    }
  };

  const toggleUsed = async (piece, e) => {
    e.stopPropagation();
    try {
      await axios.patch(`${API}/pipeline/${piece.id}`, { used: !piece.used });
      fetchPipeline();
    } catch (e) {
      console.error("Error toggling used:", e);
    }
  };

  const getPiecesForStage = (stage) => pieces.filter((p) => p.stage === stage);

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-zinc-900">Content Pipeline</h2>
          <p className="text-sm text-zinc-500 mt-1">Track your content from idea to posted</p>
        </div>
        <div className="flex gap-2 sm:gap-3 flex-wrap">
          <button
            onClick={generateDailyContent}
            disabled={loadingDaily}
            data-testid="generate-daily-btn"
            className="btn-primary flex items-center gap-2 text-sm"
          >
            <Sparkle size={18} />
            {loadingDaily ? "Generating..." : "Generate Today's Content"}
          </button>
          <button
            onClick={fetchSuggestions}
            data-testid="get-suggestions-btn"
            className="btn-secondary flex items-center gap-2 text-sm"
          >
            <Sparkle size={18} />
            Tomorrow's Ideas
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            data-testid="add-content-btn"
            className="btn-secondary flex items-center gap-2 text-sm"
          >
            <Plus size={18} />
            Add
          </button>
        </div>
      </div>

      {/* Daily Content Generated */}
      {dailyContent && (
        <div className="card p-4 sm:p-6 border-[#E91E8C] bg-pink-50/30">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Sparkle size={20} className="text-[#E91E8C]" />
              <h3 className="font-semibold text-zinc-900">
                {dailyContent.day}'s Content - {dailyContent.content_type}
              </h3>
            </div>
            <span className="text-xs text-zinc-500">Topic: {dailyContent.topic}</span>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="bg-white rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-semibold text-sm text-zinc-700">Video Script</h4>
                <CopyButton text={dailyContent.script} label="Copy" />
              </div>
              <div className="text-xs text-zinc-600 whitespace-pre-wrap max-h-48 overflow-y-auto">
                {dailyContent.script}
              </div>
            </div>
            
            <div className="bg-white rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-semibold text-sm text-zinc-700">Caption</h4>
                <CopyButton text={dailyContent.caption} label="Copy" />
              </div>
              <div className="text-xs text-zinc-600 whitespace-pre-wrap max-h-48 overflow-y-auto">
                {dailyContent.caption}
              </div>
            </div>
            
            <div className="bg-white rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-semibold text-sm text-zinc-700">LinkedIn</h4>
                <CopyButton text={dailyContent.linkedin} label="Copy" />
              </div>
              <div className="text-xs text-zinc-600 whitespace-pre-wrap max-h-48 overflow-y-auto">
                {dailyContent.linkedin}
              </div>
            </div>
          </div>
        </div>
      )}

      {loadingDaily && (
        <div className="card p-4 sm:p-6 border-[#E91E8C]/30">
          <div className="flex items-center gap-3">
            <div className="w-5 h-5 border-2 border-[#E91E8C] border-t-transparent rounded-full animate-spin"></div>
            <span className="text-zinc-600">Generating today's script, caption, and LinkedIn post...</span>
          </div>
        </div>
      )}

      {/* Month Header + Affirmation */}
      <div className="card p-4 sm:p-5 bg-gradient-to-r from-[#E91E8C]/5 to-transparent border-[#E91E8C]/10">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <div>
            <h3 className="text-2xl sm:text-3xl font-bold text-zinc-900">
              {today.toLocaleDateString("en-US", { month: "long" })} <span className="text-[#E91E8C]">{today.getFullYear()}</span>
            </h3>
            <p className="text-xs text-zinc-400 mt-0.5">{today.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}</p>
          </div>
          <div className="sm:max-w-sm sm:text-right">
            <p className="text-sm italic text-zinc-600 leading-snug">"{todayAffirmation}"</p>
          </div>
        </div>
      </div>

      {/* Week Calendar Strip */}
      <div className="card p-3 sm:p-4">
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-bold uppercase tracking-wider text-zinc-400">This Week</p>
          <p className="text-xs text-zinc-400">{today.toLocaleDateString("en-US", { month: "long", year: "numeric" })}</p>
        </div>
        <div className="grid grid-cols-7 gap-1 sm:gap-2">
          {weekDays.map((day, i) => {
            const isToday = day.toDateString() === today.toDateString();
            const dayPieces = pieces.filter((p) => {
              if (!p.date_added) return false;
              const pDate = new Date(p.date_added);
              return pDate.toDateString() === day.toDateString();
            });
            return (
              <div
                key={i}
                className={`text-center rounded-lg p-2 transition-all ${
                  isToday ? "bg-[#E91E8C] text-white" : "bg-zinc-50 hover:bg-zinc-100"
                }`}
              >
                <p className={`text-[10px] font-semibold uppercase ${isToday ? "text-white/70" : "text-zinc-400"}`}>{dayNames[i]}</p>
                <p className={`text-lg font-bold ${isToday ? "text-white" : "text-zinc-800"}`}>{day.getDate()}</p>
                {dayPieces.length > 0 && (
                  <div className={`text-[10px] font-medium mt-0.5 ${isToday ? "text-white/80" : "text-[#E91E8C]"}`}>
                    {dayPieces.length} item{dayPieces.length > 1 ? "s" : ""}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Suggestions */}
      {suggestions && (
        <div className="card p-4 sm:p-6 border-[#E91E8C]/20 bg-pink-50/50">
          <div className="flex items-center gap-2 mb-4">
            <Sparkle size={20} className="text-[#E91E8C]" />
            <h3 className="font-semibold text-zinc-900">Tomorrow's Content Suggestions</h3>
          </div>
          <div className="bg-white rounded-lg p-4 text-sm text-zinc-700 whitespace-pre-wrap">
            {suggestions.suggestions}
          </div>
        </div>
      )}

      {loadingSuggestions && (
        <div className="card p-4 sm:p-6">
          <div className="flex items-center gap-3">
            <Sparkle size={20} className="text-[#E91E8C] animate-pulse" />
            <span className="text-zinc-500">Analyzing your posting history...</span>
          </div>
        </div>
      )}

      {/* Pipeline Columns */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {stages.map((stage) => {
          const stagePieces = getPiecesForStage(stage);
          const count = stagePieces.length;
          const isExpanded = expandedStage === stage;
          const previewCount = 3;
          const visiblePieces = isExpanded ? stagePieces : stagePieces.slice(0, previewCount);
          const hasMore = count > previewCount;

          return (
            <div key={stage} className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
              {/* Column Header — clickable to expand */}
              <div
                className="flex items-center justify-between p-3 border-b border-zinc-100 cursor-pointer hover:bg-zinc-50"
                onClick={() => setExpandedStage(isExpanded ? null : stage)}
                data-testid={`stage-header-${stage}`}
              >
                <h3 className="font-semibold text-sm text-zinc-700">{stageLabels[stage]}</h3>
                <div className="flex items-center gap-1.5">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${stageColors[stage]}`}>{count}</span>
                  {hasMore && (
                    <CaretDown size={12} className={`text-zinc-400 transition-transform ${isExpanded ? "rotate-180" : ""}`} />
                  )}
                </div>
              </div>

              {/* Cards */}
              <div className={`p-2 space-y-2 ${isExpanded ? "max-h-[60vh] overflow-y-auto" : ""}`}>
                {count === 0 && (
                  <p className="text-xs text-zinc-300 text-center py-3">Empty</p>
                )}
                {visiblePieces.map((piece) => (
                  <motion.div
                    key={piece.id}
                    layout
                    initial={{ opacity: 0, y: 5 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`p-2.5 rounded-lg border group cursor-pointer transition-all hover:shadow-sm ${
                      piece.used ? "border-green-300 bg-green-50/50" : "border-zinc-100 bg-zinc-50/50 hover:border-[#E91E8C]/30"
                    }`}
                    onClick={() => setSelectedPiece(piece)}
                    data-testid={`kanban-card-${piece.id}`}
                  >
                    <div className="flex items-start justify-between gap-1.5">
                      <div className="flex items-start gap-1.5 flex-1 min-w-0">
                        <button
                          onClick={(e) => toggleUsed(piece, e)}
                          className={`mt-0.5 w-3.5 h-3.5 rounded border-2 flex items-center justify-center transition-all flex-shrink-0 ${
                            piece.used ? "bg-green-500 border-green-500 text-white" : "border-zinc-300 hover:border-[#E91E8C]"
                          }`}
                          data-testid={`toggle-used-${piece.id}`}
                        >
                          {piece.used && <Check size={8} weight="bold" />}
                        </button>
                        <p className={`text-xs font-medium leading-tight truncate ${piece.used ? "text-green-700 line-through" : "text-zinc-800"}`}>
                          {piece.title}
                        </p>
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); deletePiece(piece.id); }}
                        className="opacity-0 group-hover:opacity-100 text-zinc-300 hover:text-red-500 transition-all flex-shrink-0"
                        data-testid={`delete-piece-${piece.id}`}
                      >
                        <Trash size={12} />
                      </button>
                    </div>
                    <div className="flex items-center justify-between mt-1.5">
                      <PlatformBadge platform={piece.platform} />
                      {stage !== "posted" && (
                        <CaretRight size={12} className="text-zinc-300 group-hover:text-[#E91E8C]" />
                      )}
                    </div>
                  </motion.div>
                ))}

                {/* Show more / less toggle */}
                {hasMore && (
                  <button
                    onClick={() => setExpandedStage(isExpanded ? null : stage)}
                    className="w-full text-center py-1.5 text-[10px] font-semibold text-[#E91E8C] hover:bg-[#E91E8C]/5 rounded-lg transition-all"
                    data-testid={`toggle-expand-${stage}`}
                  >
                    {isExpanded ? "Show less" : `+${count - previewCount} more`}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Add Modal */}
      <AnimatePresence>
        {showAddModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowAddModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl"
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-bold text-zinc-900">Add New Content</h3>
                <button onClick={() => setShowAddModal(false)} className="text-zinc-400 hover:text-zinc-600">
                  <X size={20} />
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-zinc-500 block mb-2">Title</label>
                  <input
                    type="text"
                    value={newPiece.title}
                    onChange={(e) => setNewPiece({ ...newPiece, title: e.target.value })}
                    className="input-field"
                    placeholder="Content idea..."
                    data-testid="new-content-title"
                  />
                </div>
                <ChipSelector
                  label="Platform"
                  options={["tiktok", "instagram", "linkedin"]}
                  selected={newPiece.platform}
                  onChange={(p) => setNewPiece({ ...newPiece, platform: p })}
                />
                <button onClick={addPiece} className="btn-primary w-full" data-testid="submit-new-content">
                  Add to Pipeline
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Content Detail Modal */}
      <AnimatePresence>
        {selectedPiece && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setSelectedPiece(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-xl w-full max-w-lg shadow-xl max-h-[85vh] overflow-y-auto"
              data-testid="piece-detail-modal"
            >
              {/* Header */}
              <div className="sticky top-0 bg-white border-b border-zinc-100 p-4 flex items-start justify-between gap-3 z-10">
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-bold text-zinc-900 leading-tight">{selectedPiece.title}</h3>
                  <div className="flex items-center gap-2 mt-1.5">
                    <PlatformBadge platform={selectedPiece.platform} />
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${stageColors[selectedPiece.stage]}`}>
                      {stageLabels[selectedPiece.stage]}
                    </span>
                    {selectedPiece.date_added && (
                      <span className="text-[10px] text-zinc-400">
                        {new Date(selectedPiece.date_added).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                      </span>
                    )}
                  </div>
                </div>
                <button onClick={() => setSelectedPiece(null)} className="text-zinc-400 hover:text-zinc-600 p-1">
                  <X size={18} />
                </button>
              </div>

              {/* Content */}
              <div className="p-4 space-y-4">
                {selectedPiece.hook && (
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 mb-1">Hook</p>
                    <div className="bg-[#E91E8C]/5 border border-[#E91E8C]/15 rounded-lg p-3">
                      <p className="text-sm text-zinc-800 font-medium">{selectedPiece.hook}</p>
                    </div>
                  </div>
                )}

                {selectedPiece.script && (
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 mb-1">Script</p>
                    <div className="bg-zinc-50 rounded-lg p-3 max-h-48 overflow-y-auto">
                      <p className="text-sm text-zinc-700 whitespace-pre-wrap leading-relaxed">{selectedPiece.script}</p>
                    </div>
                  </div>
                )}

                {selectedPiece.source && (
                  <div className="flex items-center gap-2">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">Source</p>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                      selectedPiece.source === "auto-create" ? "bg-[#E91E8C]/10 text-[#E91E8C]" :
                      selectedPiece.source === "generated" ? "bg-purple-100 text-purple-700" :
                      "bg-zinc-100 text-zinc-600"
                    }`}>
                      {selectedPiece.source === "auto-create" ? "Auto Create" : selectedPiece.source === "generated" ? "AI Generated" : "Manual"}
                    </span>
                  </div>
                )}

                {!selectedPiece.hook && !selectedPiece.script && (
                  <div className="text-center py-6">
                    <p className="text-sm text-zinc-400">No script or hook content yet.</p>
                    <p className="text-xs text-zinc-300 mt-1">Generate a script in Script Studio or Auto Create to see content here.</p>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="border-t border-zinc-100 p-4 flex gap-2">
                {selectedPiece.stage !== "posted" && (
                  <button
                    onClick={() => { advanceStage(selectedPiece); setSelectedPiece(null); }}
                    className="btn-primary flex-1 text-sm flex items-center justify-center gap-1.5"
                    data-testid="advance-stage-btn"
                  >
                    Move to {stageLabels[stages[stages.indexOf(selectedPiece.stage) + 1]] || "Next"}
                    <CaretRight size={14} />
                  </button>
                )}
                <button
                  onClick={() => { deletePiece(selectedPiece.id); setSelectedPiece(null); }}
                  className="px-4 py-2 text-sm text-red-500 hover:bg-red-50 rounded-lg transition-all"
                  data-testid="delete-piece-modal-btn"
                >
                  <Trash size={16} />
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ContentPipeline;
